import { useState } from 'react';
import toast from 'react-hot-toast';
import {
  MessageSquare,
  X,
  Send,
  Bug,
  Lightbulb,
  TrendingUp,
  HelpCircle,
  Loader
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { GlassCard, GlassCardContent } from '@/components/ui/glass/GlassCard';
import { useAuth } from '@/hooks/useAuth';

interface FeedbackData {
  type: 'bug' | 'feature_request' | 'improvement' | 'question' | 'other';
  category?: string;
  title: string;
  description: string;
  page_url: string;
  browser_info: {
    userAgent: string;
    language: string;
    platform: string;
    screenResolution: string;
  };
}

export function FeedbackWidget() {
  const { userId, email, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState<FeedbackData>({
    type: 'bug',
    category: '',
    title: '',
    description: '',
    page_url: '',
    browser_info: {
      userAgent: '',
      language: '',
      platform: '',
      screenResolution: ''
    }
  });

  const feedbackTypes = [
    { value: 'bug', label: 'Bug Report', icon: Bug, color: 'text-red-400', bg: 'bg-red-500/20' },
    { value: 'feature_request', label: 'Feature Request', icon: Lightbulb, color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    { value: 'improvement', label: 'Improvement', icon: TrendingUp, color: 'text-green-400', bg: 'bg-green-500/20' },
    { value: 'question', label: 'Question', icon: HelpCircle, color: 'text-blue-400', bg: 'bg-blue-500/20' },
    { value: 'other', label: 'Other', icon: MessageSquare, color: 'text-purple-400', bg: 'bg-purple-500/20' }
  ];

  const categories = [
    'UI/UX',
    'Performance',
    'Data & Analytics',
    'AI Agents',
    'Campaigns',
    'Integrations',
    'Reports',
    'Other'
  ];

  const handleOpen = () => {
    setIsOpen(true);
    setSubmitted(false);
    setFormData({
      ...formData,
      page_url: window.location.href,
      browser_info: {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenResolution: `${window.screen.width}x${window.screen.height}`
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await apiClient.post('/feedback', {
        ...formData,
        user_id: userId || null,
        user_email: email || null,
        user_name: user?.given_name || null
      });

      setSubmitted(true);
      setFormData({
        type: 'bug',
        category: '',
        title: '',
        description: '',
        page_url: '',
        browser_info: formData.browser_info
      });

      // Auto-close after 2 seconds
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedTypeConfig = feedbackTypes.find(t => t.value === formData.type);

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={handleOpen}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-2 px-4 py-3 bg-purple-500/90 hover:bg-purple-500 text-white font-medium rounded-l-lg shadow-lg transition-all hover:shadow-xl hover:pr-6"
        style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
      >
        <MessageSquare className="w-5 h-5 rotate-90" />
        <span className="text-sm">Feedback</span>
      </button>

      {/* Feedback Modal/Drawer */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60">
          <GlassCard className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <GlassCardContent className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <MessageSquare className="w-6 h-6 text-purple-400" />
                    Send Feedback
                  </h2>
                  <p className="text-sm text-white/60 mt-1">
                    Help us improve by sharing your thoughts, reporting bugs, or requesting features
                  </p>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              {/* Success Message */}
              {submitted ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 border border-green-400/30 rounded-full mb-4">
                    <Send className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">Thank You!</h3>
                  <p className="text-white/70">
                    Your feedback has been submitted successfully. We'll review it soon!
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Feedback Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-3">
                      What type of feedback is this? *
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {feedbackTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = formData.type === type.value;
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, type: type.value as any })}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              isSelected
                                ? `${type.bg} border-${type.color.replace('text-', '')} border-opacity-60`
                                : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.04]'
                            }`}
                          >
                            <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? type.color : 'text-white/40'}`} />
                            <div className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-white/60'}`}>
                              {type.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Category
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 [&>option]:bg-gray-800 [&>option]:text-white"
                    >
                      <option value="" className="bg-gray-800 text-white">Select a category (optional)</option>
                      {categories.map((cat) => (
                        <option key={cat} value={cat.toLowerCase().replace(/\s/g, '_')} className="bg-gray-800 text-white">
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Brief summary of your feedback"
                      className="w-full px-4 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">
                      Description *
                    </label>
                    <textarea
                      required
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Provide detailed information about your feedback..."
                      rows={6}
                      className="w-full px-4 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                    <p className="text-xs text-white/50 mt-1">
                      Tip: Include steps to reproduce (for bugs) or use cases (for features)
                    </p>
                  </div>

                  {/* Page Context */}
                  <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-3">
                    <div className="text-xs text-white/60">
                      <strong className="text-white/80">Current Page:</strong> {window.location.pathname}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsOpen(false)}
                      className="flex-1 px-4 py-3 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-lg text-white font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || !formData.title || !formData.description}
                      className="flex-1 px-4 py-3 bg-purple-500 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader className="w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Feedback
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </GlassCardContent>
          </GlassCard>
        </div>
      )}
    </>
  );
}
